import React, { useRef, useState } from "react"

import styles from "./Slider.module.scss"

export interface SliderItem {
  title: string | JSX.Element
  value: unknown
}

interface SliderProps {
  items: SliderItem[]
  // Index de l'item sélectionné, -1 si aucune sélection
  selectedIndex: number
  onSelect: (item: SliderItem, index: number) => void
  // Question affichée au-dessus du rail ; sa présence encadre l'ensemble
  label?: React.ReactNode
}

/**
 * Échelle à crans façon jauge : rail gris, remplissage bleu animé jusqu'au
 * cran sélectionné, valeur affichée dans une puce au-dessus du curseur et
 * libellés cliquables sous le rail. Utilisable au doigt (tap ou glissé).
 * Avec `label`, la question et le rail sont présentés dans un cadre blanc.
 */
export const Slider: React.FC<SliderProps> = ({
  items,
  selectedIndex,
  onSelect,
  label,
}) => {
  const railRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)

  const positionPercent = (index: number): number =>
    items.length > 1 ? (index / (items.length - 1)) * 100 : 0

  const selectIndex = (index: number) => {
    if (index !== selectedIndex && items[index]) onSelect(items[index], index)
  }

  const indexFromClientX = (clientX: number): number | null => {
    const rect = railRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return null
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    return Math.round(ratio * (items.length - 1))
  }

  const selectFromPointer = (event: React.PointerEvent) => {
    const index = indexFromClientX(event.clientX)
    if (index !== null) selectIndex(index)
  }

  const onPointerDown = (event: React.PointerEvent) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)
    selectFromPointer(event)
  }

  const hasSelection = selectedIndex >= 0
  const fillPercent = hasSelection ? positionPercent(selectedIndex) : 0

  const slider = (
    <div className={styles.Slider}>
      <div
        className={styles.RailHitArea}
        onPointerDown={onPointerDown}
        onPointerMove={(event) => dragging && selectFromPointer(event)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
      >
        <div className={styles.Rail} ref={railRef}>
          {hasSelection && (
            <span className={styles.Chip} style={{ left: `${fillPercent}%` }}>
              {items[selectedIndex].title}
            </span>
          )}
          <div className={styles.Fill} style={{ width: `${fillPercent}%` }} />
          {items.map((item, index) => {
            const isHandle = index === selectedIndex
            const isFilled = hasSelection && index < selectedIndex
            return (
              <span
                key={index}
                className={`${styles.Dot} ${isFilled ? styles.DotFilled : ""} ${
                  isHandle ? styles.Handle : ""
                }`}
                style={{ left: `${positionPercent(index)}%` }}
              />
            )
          })}
        </div>
      </div>

      <div className={styles.Labels}>
        {items.map((item, index) => (
          <span
            key={index}
            className={`${styles.Label} ${
              index === selectedIndex ? styles.LabelSelected : ""
            }`}
            style={{ left: `${positionPercent(index)}%` }}
            onClick={() => selectIndex(index)}
          >
            {item.title}
          </span>
        ))}
      </div>
    </div>
  )

  if (!label) return slider

  return (
    <div className={styles.Frame}>
      <div className={styles.FrameLabel}>{label}</div>
      {slider}
    </div>
  )
}

export default Slider
